// 🔧 pages/login.js
// ✅ Purpose: Styled login page matching public site layout
// ✅ Features:
// - Supports QR vote context
// - Success + error messages
// - Email/password + Google login
// - Registration, password reset, resend verification
// - Redirects to correct page after login
// - Completes pending manager acceptance through a trusted callable

import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db, functions } from "../../firebase";
import { httpsCallable } from "firebase/functions";
import {
  signInWithEmailAndPassword,
  signOut,
  signInWithPopup,
  getRedirectResult,
  GoogleAuthProvider,
  FacebookAuthProvider,
} from "firebase/auth";
import {
  doc,
  setDoc,
  serverTimestamp,
  getDoc,
  getDocs,
  collection,
  query,
  where,
  limit,
} from "firebase/firestore";
import { showStatus } from "../../utils/status_helpers";
import { logger } from '../../utils/logger';
import {
  SESSION_KEYS,
  getSessionValue,
  setSessionValue,
  removeSessionValue,
} from "../../utils/browser_storage_helpers";
import SeoHead from "../../components/seo_head";
import { PUBLIC_PAGE_SEO } from "../../constants/marketing_pages";
import { trackEvent } from "../../utils/analytics";
import { GA_EVENTS } from "../../services/analytics/ga_events";

function Login() {

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState(null);
  const [user, loading] = useAuthState(auth);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const cid = searchParams.get("cid");
  const [pendingVote, setPendingVote] = useState(null);
  const [categoryName, setCategoryName] = useState("");

  const linkVerifiedPendingUser = async (firebaseUser) => {
    if (!firebaseUser?.emailVerified) return;

    try {
      await firebaseUser.getIdToken(true);
      await httpsCallable(functions, "linkPendingUserToUser")({});
    } catch (error) {
      // Login remains available; a subsequent verified login can retry the
      // idempotent invitation claim.
      logger.warn("Pending invitation linking will be retried later.", {
        code: error?.code || "",
      });
    }
  };
  const isVote = !!getSessionValue(
    SESSION_KEYS.PENDING_VOTE_CID
  ); 
  const hasRedirected = useRef(false);
  const [pendingVoteEntryLabel, setPendingVoteEntryLabel] = useState("");
  const [pendingVoteCategoryLabel, setPendingVoteCategoryLabel] = useState("");

  useEffect(() => {
    setPendingVoteEntryLabel(
      getSessionValue(SESSION_KEYS.PENDING_VOTE_ENTRY_LABEL) || ""
    );

    setPendingVoteCategoryLabel(
      getSessionValue(SESSION_KEYS.PENDING_VOTE_CATEGORY_LABEL) || ""
    );
  }, []);

  useEffect(() => {
    const checkRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);

        if (result?.user) {
          logger.log("🔁 Redirect login result:", {
            uid: result.user.uid,
          });

          const userRef = doc(db, "users", result.user.uid);

          await setDoc(
            userRef,
            {
              uid: result.user.uid,
              email: result.user.email,
              firstName:
                result.user.displayName?.split(" ")[0] || "",
              lastName:
                result.user.displayName?.split(" ").slice(1).join(" ") || "",
              role: "voter",
              lastLogin: serverTimestamp(),
              createdAt: serverTimestamp(),
            },
            { merge: true }
          );
        }
      } catch (err) {
        logger.error("❌ Redirect login failed:", {
          value: err,
        });
      }
    };

    checkRedirectResult();
  }, []);

  useEffect(() => {

    if (loading) return;
  
      const voteParam = searchParams.get("vote");
      const msgParam = searchParams.get("msg");
      const resetParam = searchParams.get("reset");

      // ----------------------------------------------------
      // ✅ Show warning persisted through SSO sign-out
      // ----------------------------------------------------

      const storedLoginWarning = getSessionValue(
        SESSION_KEYS.LOGIN_WARNING
      );

      if (storedLoginWarning) {
        showStatus(setStatus, {
          type: "warning",
          message: storedLoginWarning,
        });

        removeSessionValue(
          SESSION_KEYS.LOGIN_WARNING
        );

      }

    if (msgParam === "ssoNotConnected") {
      const providerParam = searchParams.get("provider") || "this login method";

      showStatus(setStatus, {
        type: "warning",
        message: `${providerParam} is not connected to a Let's Go Vote account. Please log in using the method you used when you created your account.`,
      });

      const newParams = new URLSearchParams(searchParams);
      newParams.delete("msg");
      newParams.delete("provider");

      navigate(`/login?${newParams.toString()}`, {
        replace: true,
      });

      return;
    }

    if (resetParam === "success") {

      showStatus(setStatus, {
        type: "success",
        message: "Your password has been reset. Please log in.",
      });

    }

    if (voteParam) {
      logger.log("🗳️ QR vote param detected", {});

      showStatus(setStatus, {
        type: "info",
        message: "Your vote is ready to be submitted. Complete login to continue.",
      });

    }
  
    if (msgParam === "emailVerified") {

      showStatus(setStatus, {
        type: "success",
        message: "Thanks for confirming your email! Login and start voting.",
      });

      const newParams = new URLSearchParams(searchParams);
      newParams.delete("msg");
      navigate(`/login?${newParams.toString()}`, { replace: true });
      return;
    }
    
    // ✅ If redirected here from role_guard or register
    if (msgParam === "emailVerify") {

      showStatus(setStatus, {
        type: "warning",
        message:
          "Your account has been registered. Please check your email and verify your account before logging in.",
      });

    }
  
    if (location.state?.successMsg) {
      showStatus(setStatus, {
        type: "success",
        message: location.state.successMsg,
      });

      window.history.replaceState({}, document.title);
    }

    if (!user) return;
  
    // ✅ Exit early if user is logged in but not verified

    if (!user?.emailVerified && !loading) {
      logger.log("📩 Unverified user detected on login page. Redirecting to dashboard.", {});

      navigate("/dashboard?verifyRequired=1", {
        replace: true,
      });

      return;
    }
  
    const pendingManagerSlug = getSessionValue(
      SESSION_KEYS.PENDING_MANAGER_ACCEPT
    );

    if (pendingManagerSlug && !hasRedirected.current) {
      hasRedirected.current = true;

      const run = async () => {
        try {
          const eventQuery = query(
            collection(db, "events"),
            where("slug", "==", pendingManagerSlug),
            limit(1)
          );

          const eventSnap = await getDocs(eventQuery);

          if (eventSnap.empty) {
            throw new Error("Event not found");
          }

          const eventId = eventSnap.docs[0].id;

          await httpsCallable(functions, "acceptManagerInvite")({ eventId });
  
          removeSessionValue(
            SESSION_KEYS.PENDING_MANAGER_ACCEPT
          );

          setSessionValue(
            SESSION_KEYS.MANAGER_ACCEPTED_EVENT_NAME,
            pendingManagerSlug || "this event"
          );

          navigate("/dashboard?login1", { replace: true });
        } catch (err) {
          logger.error("❌ Error confirming manager invite:", {
            value: err
          });

          showStatus(setStatus, {
            type: "error",
            message: "Could not confirm your manager invitation. Please try again.",
          });

        }
      };
      run();
      return;
    }
  
    const eventSlug = getSessionValue(
      SESSION_KEYS.LAST_EVENT_SLUG
    );

    const pendingCid = getSessionValue(
      SESSION_KEYS.PENDING_VOTE_CID
    );

    if (!hasRedirected.current) {
      hasRedirected.current = true;

      const redirectParam = searchParams.get("redirect");

      if (redirectParam) {
        navigate(redirectParam, { replace: true });
        return;
      }

      if (pendingCid && eventSlug) {

        removeSessionValue(
          SESSION_KEYS.PENDING_VOTE_CID
        );        

        logger.log("Navigate to Vote", {});
        navigate(`/${eventSlug}/vote/${pendingCid}`, { replace: true });
      } else if (eventSlug) {
        logger.log("Navigate to Ballot", {});
        navigate(`/${eventSlug}/ballot`, { replace: true });
      } else {
        navigate("/dashboard?login2", { replace: true });
      }
    }  

  }, [user, loading, navigate, location, searchParams]);
 
  useEffect(() => {

    const pendingCid = getSessionValue(
      SESSION_KEYS.PENDING_VOTE_CID
    );

    const eventId = getSessionValue(
      SESSION_KEYS.LAST_EVENT_ID
    );

    if (!pendingCid || !eventId) {
      logger.warn("⚠️ Missing pending vote session values:", {
        pendingCid,
        eventId,
      });

      return;
    }

    const fetchCar = async () => {
      try {  
        const carRef = doc(db, "events", eventId, "cars", pendingCid);
        const snap = await getDoc(carRef);
  
        if (!snap.exists()) {
          logger.warn("❌ Car not found:", {
            value: pendingCid
          });
          return;
        }
  
        const carData = snap.data();
        setPendingVote(carData);
  
        if (carData.catid) {
          const catRef = doc(db, "events", eventId, "categories", String(carData.catid));
          const catSnap = await getDoc(catRef);
          if (catSnap.exists()) {
            setCategoryName(catSnap.data().name || "Unknown");
          }
        }
      } catch (err) {
        logger.error("❗ Failed to load pending vote in login.js:", {
          value: err
        });
      }
    };
  
    fetchCar();
  }, []);
  
  const handleLogin = async (e) => {
    e.preventDefault();
    
    setStatus(null);

    try {
      const userCred = await signInWithEmailAndPassword(auth, email, password);

      trackEvent({
        action: GA_EVENTS.LOGIN_SUCCESS,
        parameters: {
          method: "email",
          source_page: window.location.pathname,
        },
      });

      if (!userCred.user.emailVerified) {
        navigate("/dashboard?verifyRequired=1", {
          state: {
            successMsg:
              "📩 Please verify your email before voting. We've sent a verification email to your inbox.",
          },
          replace: true,
        });

        return;
      }

      await linkVerifiedPendingUser(userCred.user);

    } catch (err) {
      logger.error("❌ Login failed:", {
        value: err
      });

      showStatus(setStatus, {
        type: "error",
        message: `Login failed: ${err.message}`,
      });

    }
  };

  const handleGoogleLogin = async () => {
    const provider = new GoogleAuthProvider();

    try {

      const result = await signInWithPopup(auth, provider);
      const googleUser = result.user;

      logger.log("🔍 Google login user", {
        uid: googleUser.uid,
        email: googleUser.email,
      });

      const userRef = doc(db, "users", googleUser.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        logger.warn("⚠️ Google login blocked — no matching LGV user doc", {
          uid: googleUser.uid,
          email: googleUser.email,
        });

        setSessionValue(
          SESSION_KEYS.LOGIN_WARNING,
          `An account already exists for ${googleUser.email}, and is not associated with your Google sign in.
          
        Please log in using the method you originally used to create your Let's Go Vote account.

        If you registered with Email & Password, use Email & Password to sign in.`
        );

        await signOut(auth);

        navigate("/login", { replace: true });

        return;
      }

      await setDoc(
        userRef,
        {
          providerGoogle: true,
          lastLogin: serverTimestamp(),
        },
        { merge: true }
      );

      await linkVerifiedPendingUser(googleUser);

      logger.log("✅ Google login allowed", {
        uid: googleUser.uid,
      });

      trackEvent({
        action: GA_EVENTS.LOGIN_SUCCESS,
        parameters: {
          method: "google",
          source_page: window.location.pathname,
        },
      });

    } catch (err) {
      logger.error("❌ Google login failed:", { value: err });

        if (err.code === "auth/account-exists-with-different-credential") {
          showStatus(setStatus, {
            type: "warning",
            message:
              "This email already has a Let's Go Vote account. Please log in using the method you used when you created the account.",
          });

          return;
        }

        showStatus(setStatus, {
          type: "error",
          message: `Google login failed: ${err.message}`,
        });

    }
  };

  // =====================================================
  // ✅ Facebook Login
  // =====================================================
  const handleFacebookLogin = async () => {
    const provider = new FacebookAuthProvider();

    try {

      const result = await signInWithPopup(auth, provider);
      const facebookUser = result.user;

      logger.log("🔍 Facebook login user", {
        uid: facebookUser.uid,
        email: facebookUser.email,
      });

      const userRef = doc(db, "users", facebookUser.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        logger.warn("⚠️ Facebook login blocked — no matching LGV user doc", {
          uid: facebookUser.uid,
          email: facebookUser.email,
        });

        setSessionValue(
          SESSION_KEYS.LOGIN_WARNING,
          `An account already exists for ${facebookUser.email}, and is not associated with your Meta/Facebook sign in.
          
        Please log in using the method you originally used to create your Let's Go Vote account.

        If you registered with Email & Password, use Email & Password to sign in.`
        );

        await signOut(auth);

        navigate("/login", { replace: true });

        return;
      }

      await setDoc(
        userRef,
        {
          providerFacebook: true,
          lastLogin: serverTimestamp(),
        },
        { merge: true }
      );

      await linkVerifiedPendingUser(facebookUser);

      logger.log("✅ Facebook login allowed", {
        uid: facebookUser.uid,
      });

      trackEvent({
        action: GA_EVENTS.LOGIN_SUCCESS,
        parameters: {
          method: "facebook",
          source_page: window.location.pathname,
        },
      });

    } catch (err) {
      logger.error("❌ Facebook login failed:", { value: err });

      if (err.code === "auth/account-exists-with-different-credential") {
        showStatus(setStatus, {
          type: "warning",
          message:
            "This email already has a Let's Go Vote account. Please log in using the method you used when you created the account.",
        });

        return;
      }

      showStatus(setStatus, {
        type: "error",
        message: `Facebook login failed: ${err.message}`,
      });
    }
  };

return (
  <>
    <SeoHead {...PUBLIC_PAGE_SEO.login} />
    <section id="top-content" className="container-fluid">
      <div className="container">
        <div className="row justify-content-center text-center">
          <div className="col-xs-12">
            <h1 className="text-white mb-3">Log In</h1>
            <h4 className="text-white-50 mb-4">
              Login to vote, manage your entries, or accept a volunteer invite.
            </h4>

          </div>

          <div className="col-xs-12 col-md-6">

            <div className="bg-white p-4 rounded shadow">

              {isVote && (
                <div className="alert alert-warning text-start mt-4 shadow-sm">
                  <strong>🗳️ Your vote is saved</strong>

                  {" "}for{" "}
                  <strong>
                    {pendingVoteEntryLabel ||
                      pendingVote?.description ||
                      `${pendingVote?.year || ""} ${pendingVote?.model || ""}`.trim() ||
                      "this entry"}
                  </strong>

                  {" "}in the{" "}
                  <strong>
                    {pendingVoteCategoryLabel || categoryName || "Unknown"}
                  </strong>
                  {" "}category.

                  <br />
                  Please log in to confirm your submission and continue voting.
                </div>
              )}

              {status?.message && (
                <div
                  role="alert"
                  className={`alert ${
                    status.type === "success"
                      ? "alert-success"
                      : status.type === "warning"
                      ? "alert-warning"
                      : status.type === "error"
                      ? "alert-danger"
                      : "alert-info"
                  }`}
                >
                  {status.message}
                </div>
              )}

              {/* =====================================================
                  🔐 Social Login Options
              ===================================================== */}
              <div className="mb-4 text-center">
                <div className="d-grid gap-2">
                  <button
                    type="button"
                    onClick={handleGoogleLogin}
                    className="btn btn-primary"
                  >
                    🔐 Continue with Google
                  </button>

                  <button
                    type="button"
                    onClick={handleFacebookLogin}
                    className="btn btn-primary"
                  >
                    📘 Continue with Facebook
                  </button>
                </div>

                <p className="form-text mt-3 text-muted">
                  Continue with Google or Facebook for faster sign in.
                </p>
              </div>

              {/* 📝 Register link helper */}
              <p className="text-center text-muted mb-3">
                New here?{" "}
                <Link
                  to={`/register${cid ? `?cid=${encodeURIComponent(cid)}` : ""}`}
                  className="fw-bold text-decoration-none"
                >
                  Register an account
                </Link>
              </p>

              {/* 💬 Separator */}
              <hr className="my-4" />
              <h5 className="text-center mb-3">Log in with email</h5>

              {/* 🔐 Email/password login */}
              <form onSubmit={handleLogin}>
                <div className="mb-3">
                  <input
                    type="email"
                    className="form-control"
                    placeholder="Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoFocus
                  />
                </div>

                <div className="mb-3">
                  <input
                    type="password"
                    className="form-control"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>

                <button type="submit" className="btn btn-success w-100 mb-3">
                  Log In
                </button>

                <div className="mt-3 text-center">
                  <Link to="/resend_verification">Resend Verification Email</Link>
                </div>
                
                <div className="mt-2 text-center">
                  <Link to="/reset">Forgot Password?</Link>
                </div>
              </form>

            </div>
          </div>
        </div>
      </div>
    </section>
  </>
  );
}

export default Login;
